import VueRouter, { RawLocation, Route, NavigationFailureType, isNavigationFailure } from 'vue-router';

export type NavigationResult = {
    type: 'completed' | 'unchanged' | 'blocked' | 'cancelled' | 'failed' | 'obsolete';
    route?: Route;
    error?: Error;
};
export interface NavigationHost {
    resolve: (run: () => Promise<NavigationResult>) => Promise<NavigationResult>;
    needsUnloadProtection: () => boolean;
    cancelPending?: () => void;
}
export interface NavigationRegistration {
    dispose: () => void;
    continueNavigation: (location: RawLocation, mode?: 'push' | 'replace') => Promise<NavigationResult>;
    continueHandoff: (target: string, run: () => void | Promise<void>) => Promise<NavigationResult>;
}
export interface EditSessionNavigation {
    registerHost: (host: NavigationHost) => NavigationRegistration;
    navigate: (location: RawLocation, mode?: 'push' | 'replace') => Promise<NavigationResult>;
    handoff: (target: string, run: () => void | Promise<void>) => Promise<NavigationResult>;
    traverse: (delta: number) => Promise<NavigationResult>;
    refreshUnloadProtection: () => void;
    dispose: () => void;
}

export const editNavigationStateKey = '__sobEditNavigation';
// Bounds missing platform/router acknowledgements, never time spent choosing Save/Discard/Keep.
export const navigationRecoveryTimeoutMs = 5000;
interface Entry { chain: string; id: string; position: number; navigationKey?: string }
interface BrowserNavigation {
    currentEntry?: { key: string; index: number };
    traverseTo: (key: string) => { finished: Promise<unknown> };
}
interface RegisteredHost { host: NavigationHost; live: boolean }
interface Request {
    origin: Route;
    owner?: RegisteredHost;
    hosts: RegisteredHost[];
    routing: boolean;
    cancelled: boolean;
    redirected?: boolean;
    followup?: () => void;
    abort: (result: NavigationResult) => void;
}
interface RouterHistory {
    pending: Route | null;
    push: (location: RawLocation, complete?: (route: Route) => void, abort?: (error: Error) => void) => void;
    replace: RouterHistory['push'];
    transitionTo: RouterHistory['push'];
    ensureURL: (push?: boolean) => void;
}
const adapters = new WeakMap<VueRouter, EditSessionNavigation>();
export const getEditSessionNavigation = (router: VueRouter): EditSessionNavigation | undefined => adapters.get(router);

/** Observe completion for parent/action consumers, including shells without installed interception. */
export function navigateEditSessionTarget(router: VueRouter, location: RawLocation, registration?: NavigationRegistration): Promise<NavigationResult> {
    if (registration) return registration.continueNavigation(location);
    const adapter = getEditSessionNavigation(router);
    if (adapter) return adapter.navigate(location);
    return new Promise(resolve => router.push(location,
        route => resolve({ type: 'completed', route }),
        error => resolve({ type: isNavigationFailure(error, NavigationFailureType.duplicated) ? 'unchanged' :
            isNavigationFailure(error) ? 'cancelled' : 'failed', error })));
}

/** Explicit main-app bootstrap only. There are no listeners, writers or host registrations at import time. */
export function installEditSessionNavigation(router: VueRouter): EditSessionNavigation {
    const installed = adapters.get(router);
    if (installed) return installed;
    const history = (router as unknown as { history: RouterHistory }).history;
    const hosts: RegisteredHost[] = [];
    let pending: Request | null = null;
    let disposed = false;
    let sequence = 0;
    const chain = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const navigationCandidate = (window as unknown as { navigation?: BrowserNavigation }).navigation;
    const browserNavigation = typeof navigationCandidate?.traverseTo === 'function' ? navigationCandidate : undefined;
    const nativeIdentity = () => {
        const entry = browserNavigation?.currentEntry;
        return entry && entry.key && entry.index >= 0 ? entry : null;
    };
    const createEntry = (position: number): Entry => ({ chain, id: `${chain}-${++sequence}`, position });
    const readEntry = (): Entry | null => {
        const entry = window.history.state && window.history.state[editNavigationStateKey];
        const native = nativeIdentity();
        if (entry && entry.chain === chain && typeof entry.id === 'string' && Number.isInteger(entry.position)) {
            return { ...entry, navigationKey: native?.key };
        }
        return native ? { chain: 'navigation-api', id: native.key, position: native.index, navigationKey: native.key } : null;
    };
    const sameEntry = (a: Entry | null, b: Entry | null) => !!a && !!b &&
        (a.navigationKey && b.navigationKey ? a.navigationKey === b.navigationKey : a.chain === b.chain && a.id === b.id);
    const entries = new Map<number, Entry>();
    const pushState = window.history.pushState;
    const replaceState = window.history.replaceState;
    let committed: Entry | null = null;
    let permit: { request: Request; path: string; consumed: boolean } | null = null;
    let arrival: { entry: Entry; request: Request | null; resolve: (ok: boolean) => void; timer: ReturnType<typeof setTimeout>; route: boolean } | null = null;
    let routingArrival: { request: Request; finish: (result: NavigationResult) => void } | null = null;
    let recovery: Promise<boolean> | null = null;
    let unloadAttached = false;
    let leavePermit: { target: string; request: Request } | null = null;

    const classify = (error: Error): NavigationResult => ({
        type: isNavigationFailure(error, NavigationFailureType.duplicated) ? 'unchanged' :
            isNavigationFailure(error) ? 'cancelled' : 'failed', error,
    });
    const failure = (message: string): NavigationResult => ({ type: 'failed', error: new Error(message) });
    const asError = (result: NavigationResult, to?: RawLocation): Error => {
        if (result.error) return result.error;
        return Object.assign(new Error(`Edit navigation ${result.type}`), {
            _isRouter: true, type: NavigationFailureType.aborted, from: router.currentRoute,
            to: to ? router.resolve(to).route : router.currentRoute,
        });
    };
    const beforeUnload = (event: BeforeUnloadEvent) => {
        if (leavePermit && pending === leavePermit.request) { leavePermit = null; return; }
        if (hosts.some(item => item.live && item.host.needsUnloadProtection())) {
            event.preventDefault();
            event.returnValue = '';
        }
    };
    const refreshUnloadProtection = () => {
        const needed = hosts.some(item => item.live && item.host.needsUnloadProtection());
        if (needed === unloadAttached) return;
        unloadAttached = needed;
        if (needed) window.addEventListener('beforeunload', beforeUnload);
        else window.removeEventListener('beforeunload', beforeUnload);
    };
    const stamp = (state: unknown, title: string, url: string | URL | null | undefined, replace: boolean) => {
        const observed = readEntry();
        const previous = observed?.chain === chain ? observed : null;
        const entry = replace && previous ? previous : createEntry(replace ? 0 : (previous ? previous.position + 1 : 0));
        const nextState = { ...(state as object), [editNavigationStateKey]: entry };
        // Update bookkeeping only after the browser accepts the write.
        (replace ? replaceState : pushState).call(window.history, nextState, title, url);
        if (!replace) Array.from(entries.keys()).filter(position => position >= entry.position).forEach(position => entries.delete(position));
        entries.set(entry.position, entry);
        committed = readEntry();
    };
    const wrappedPushState: History['pushState'] = (state, title, url) => stamp(state, title, url, false);
    const wrappedReplaceState: History['replaceState'] = (state, title, url) => stamp(state, title, url, true);
    // A failed initial stamp is a native compatibility limitation; ordinary app navigation still installs.
    try { stamp(window.history.state, '', undefined, true); } catch (error) { console.error('Edit navigation entry initialization failed', error); }
    window.history.pushState = wrappedPushState;
    window.history.replaceState = wrappedReplaceState;

    const stopArrival = () => {
        if (!arrival) return;
        const current = arrival;
        arrival = null;
        clearTimeout(current.timer);
        current.resolve(false);
    };
    const cancelRequest = (result: NavigationResult) => {
        const request = pending;
        if (!request) return;
        request.cancelled = true;
        permit = null;
        leavePermit = null;
        if (request.routing) history.pending = null;
        stopArrival();
        if (routingArrival) { routingArrival.finish(result); routingArrival = null; }
        const owners = request.owner ? [request.owner, ...request.hosts] : request.hosts;
        owners.forEach(item => item.host.cancelPending && item.host.cancelPending());
        request.abort(result);
    };

    async function request(run: (request: Request) => Promise<NavigationResult>, skip?: RegisteredHost, beforeHosts?: (current: Request) => Promise<boolean>): Promise<NavigationResult> {
        if (disposed) return { type: 'obsolete' };
        if (pending) return { type: 'blocked' };
        let abort: (result: NavigationResult) => void;
        const cancelled = new Promise<NavigationResult>(resolve => { abort = resolve; });
        const current: Request = { origin: router.currentRoute, owner: skip, hosts: hosts.filter(item => item.live && item !== skip), routing: false, cancelled: false, abort: result => abort(result) };
        pending = current;
        const resolveHost = async (index: number): Promise<NavigationResult> => {
            if (current.cancelled || disposed) return { type: 'obsolete' };
            if (index === current.hosts.length) return run(current);
            const item = current.hosts[index];
            if (!item.live) return { type: 'obsolete' };
            let invoked = false;
            return item.host.resolve(() => {
                if (invoked) return Promise.resolve({ type: 'blocked' });
                invoked = true;
                return resolveHost(index + 1);
            });
        };
        try {
            const resolution = beforeHosts ? beforeHosts(current).then(ok => ok ? resolveHost(0) : failure('History origin was not restored')) : resolveHost(0);
            return await Promise.race([resolution, cancelled]);
        }
        catch (error) { return { type: 'failed', error: error as Error }; }
        finally {
            if (pending === current) { pending = null; permit = null; leavePermit = null; }
            refreshUnloadProtection();
            if (!current.cancelled && current.followup) current.followup();
        }
    }

    const originalPush = router.push;
    const originalReplace = router.replace;
    const originalGo = router.go;
    const originalBack = router.back;
    const originalForward = router.forward;
    const originalHistoryPush = history.push;
    const originalHistoryReplace = history.replace;
    const originalTransition = history.transitionTo;
    const originalEnsureURL = history.ensureURL;
    history.ensureURL = push => {
        // Router abort recovery normally pushes a URL. Traversal recovery must preserve entries.
        if (!routingArrival) originalEnsureURL.call(history, push);
    };

    async function route(location: RawLocation, mode: 'push' | 'replace', current: Request): Promise<NavigationResult> {
        if (recovery && !await recovery) return failure('History origin was not restored');
        if (pending !== current || current.cancelled) return Promise.resolve({ type: 'obsolete' });
        current.routing = true;
        const path = router.resolve(location).route.fullPath;
        permit = { request: current, path, consumed: false };
        return new Promise(resolve => {
            let settled = false;
            const finish = (result: NavigationResult) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                resolve(result);
            };
            const timer = setTimeout(() => {
                if (pending === current) { history.pending = null; permit = null; }
                finish(failure('Router completion timed out'));
            }, navigationRecoveryTimeoutMs);
            try {
                (mode === 'push' ? originalPush : originalReplace).call(router, location,
                    (completed: Route) => finish(completed.fullPath === path ? { type: 'completed', route: completed } : failure('Unexpected route destination')),
                    (error: Error) => {
                        current.redirected = isNavigationFailure(error, NavigationFailureType.redirected);
                        finish(classify(error));
                    });
            } catch (error) { finish({ type: 'failed', error: error as Error }); }
        });
    }
    const capture = (location: RawLocation): RawLocation => {
        const resolved = router.resolve(location).location;
        return { ...resolved, query: resolved.query && JSON.parse(JSON.stringify(resolved.query)), params: resolved.params && { ...resolved.params } };
    };
    const navigate = (location: RawLocation, mode: 'push' | 'replace' = 'push', skip?: RegisteredHost) => {
        try {
            const target = capture(location);
            return request(current => route(target, mode, current), skip);
        } catch (error) {
            return Promise.resolve<NavigationResult>({ type: 'failed', error: error as Error });
        }
    };
    const handoff = (target: string, run: () => void | Promise<void>, skip?: RegisteredHost) => request(async current => {
        current.routing = true;
        leavePermit = { target, request: current };
        try {
            const completion = run();
            leavePermit = null;
            await completion;
            return { type: 'completed' };
        } finally { leavePermit = null; }
    }, skip);
    const ingress = (mode: 'push' | 'replace') => ((location: RawLocation, complete?: (route: Route) => void, abort?: (error: Error) => void) => {
        const result = navigate(location, mode);
        if (complete || abort) {
            void result.then(outcome => {
                if (outcome.type === 'completed') { if (complete) complete(outcome.route); }
                else if (abort) abort(asError(outcome, location));
            });
            return;
        }
        return result.then(outcome => outcome.type === 'completed' ? outcome.route : Promise.reject(asError(outcome, location)));
    }) as VueRouter['push'];
    router.push = ingress('push');
    router.replace = ingress('replace');

    // Redirects and legacy callers can enter the history object without public router ingress.
    const historyIngress = (mode: 'push' | 'replace'): RouterHistory['push'] => (location, complete, abort) => {
        const path = router.resolve(location).route.fullPath;
        if (pending && permit && permit.request === pending && !permit.consumed && permit.path === path) {
            (mode === 'push' ? originalHistoryPush : originalHistoryReplace).call(history, location, complete, abort);
        } else if (pending) {
            if (pending.redirected && pending.hosts.length === 0 && !hosts.some(item => item.live)) {
                const target = capture(location);
                // Preserve existing access redirects when there was no protected host to abandon.
                pending.followup = () => historyIngress(mode)(target, complete, abort);
            } else if (abort) abort(asError({ type: 'blocked' }, location));
        } else {
            void navigate(location, mode).then(result => {
                if (result.type === 'completed') { if (complete) complete(result.route); }
                else if (abort) abort(asError(result, location));
            });
        }
    };
    history.push = historyIngress('push');
    history.replace = historyIngress('replace');

    const removeGuard = router.beforeEach((to, from, next) => {
        if (permit && pending === permit.request && !permit.consumed && permit.path === to.fullPath) {
            permit.consumed = true;
            next();
        } else if (pending) next(false);
        else if (!hosts.some(item => item.live)) next();
        else {
            next(false);
            // An unadapted transition has no public caller. Capture only this first destination.
            void navigate(to.fullPath).then(result => {
                if (result.type === 'failed') console.error('Edit navigation failed', result.error);
            });
        }
    });

    const moveTo = (entry: Entry, current: Request | null, admitRoute = false): Promise<boolean> => {
        if (arrival) return Promise.resolve(false);
        const physical = readEntry();
        if (!physical || (physical.chain !== entry.chain && !(browserNavigation && entry.navigationKey))) return Promise.resolve(false);
        if (sameEntry(physical, entry)) return Promise.resolve(true);
        return new Promise(resolve => {
            const waiting = {
                entry, request: current, route: admitRoute, resolve,
                timer: setTimeout(() => {
                    if (arrival === waiting) { arrival = null; resolve(false); }
                }, navigationRecoveryTimeoutMs),
            };
            arrival = waiting;
            try {
                if (physical.chain !== entry.chain && browserNavigation && entry.navigationKey) {
                    void browserNavigation.traverseTo(entry.navigationKey).finished.catch(() => {
                        if (arrival === waiting) stopArrival();
                    });
                } else window.history.go(entry.position - physical.position);
            }
            catch (error) { stopArrival(); }
        });
    };
    function replay(entry: Entry, current: Request): Promise<NavigationResult> {
        current.routing = true;
        return new Promise(resolve => {
            let settled = false;
            const finish = (result: NavigationResult) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                if (routingArrival && routingArrival.request === current) routingArrival = null;
                resolve(result);
            };
            const timer = setTimeout(() => { history.pending = null; finish(failure('History route completion timed out')); }, navigationRecoveryTimeoutMs);
            routingArrival = { request: current, finish };
            void moveTo(entry, current, true).then(ok => { if (!ok) finish(failure('History arrival was not acknowledged')); });
        });
    }
    history.transitionTo = (location, complete, abort) => {
        const observed = routingArrival;
        originalTransition.call(history, location, route => {
            if (complete) complete(route);
            if (observed && pending === observed.request) {
                committed = readEntry();
                observed.finish({ type: 'completed', route });
            }
        }, error => {
            if (abort) abort(error);
            if (observed && pending === observed.request) observed.finish(classify(error));
        });
    };
    const nativePop = (event: PopStateEvent) => {
        const entry = readEntry();
        if (arrival) {
            const waiting = arrival;
            if (!sameEntry(entry, waiting.entry)) { event.stopImmediatePropagation(); return; }
            arrival = null;
            clearTimeout(waiting.timer);
            if (waiting.route && waiting.request && pending === waiting.request && !waiting.request.cancelled) {
                permit = { request: waiting.request, path: window.location.pathname + window.location.search + window.location.hash, consumed: false };
            } else event.stopImmediatePropagation();
            waiting.resolve(true);
            return;
        }
        if (!pending && !hosts.some(item => item.live)) { committed = entry; return; }
        const origin = committed;
        if ((!origin || !entry) && !pending && !hosts.some(item => item.live && item.host.needsUnloadProtection())) {
            // No draft or pending write needs restoration. Resolve the clean hosts, then
            // consume the URL the browser already reached without adding a history entry.
            event.stopImmediatePropagation();
            const path = window.location.pathname + window.location.search + window.location.hash;
            void request(current => {
                current.routing = true;
                permit = { request: current, path, consumed: false };
                return new Promise(resolve => {
                    const timer = setTimeout(() => {
                        if (pending === current) history.pending = null;
                        resolve(failure('Router completion timed out'));
                    }, navigationRecoveryTimeoutMs);
                    originalTransition.call(history, path, route => {
                        clearTimeout(timer);
                        if (pending !== current || current.cancelled) { resolve({ type: 'obsolete' }); return; }
                        committed = readEntry();
                        resolve({ type: 'completed', route });
                    }, error => { clearTimeout(timer); resolve(classify(error)); });
                });
            }).then(result => { if (result.type === 'failed') console.error('Edit history navigation failed', result.error); });
            return;
        }
        event.stopImmediatePropagation();
        if (!origin || !entry) {
            console.error('Edit history navigation failed', new Error('History entry identity is unavailable'));
            cancelRequest(failure('History entry identity is unavailable'));
            return;
        }
        if (pending) {
            if (!recovery) {
                const restoring = moveTo(origin, pending);
                recovery = restoring;
                void restoring.then(ok => {
                    if (recovery === restoring) recovery = null;
                    if (!ok) cancelRequest(failure('Competing history traversal could not restore origin'));
                });
            }
            return;
        }
        // The adapter reserves ingress before restoration. Host choice opens only at the origin.
        const completion = request(async current => {
            const result = await replay(entry, current);
            if (result.type !== 'completed') await moveTo(origin, current);
            return result;
        }, undefined, current => moveTo(origin, current));
        void completion.then(result => { if (result.type === 'failed') console.error('Edit history navigation failed', result.error); });
    };
    window.addEventListener('popstate', nativePop, true);
    const traverse = (delta: number): Promise<NavigationResult> => {
        if (pending) return Promise.resolve({ type: 'blocked' });
        const origin = readEntry();
        const target = origin && entries.get(origin.position + delta);
        if (!delta) return Promise.resolve({ type: 'unchanged' });
        if (!target) {
            // Unknown/cross-document traversal stays browser-owned, including beforeunload.
            if (hosts.some(item => item.live)) return Promise.resolve({ type: 'blocked' });
            originalGo.call(router, delta);
            return Promise.resolve({ type: 'unchanged' });
        }
        return request(async current => {
            const result = await replay(target, current);
            if (result.type !== 'completed') await moveTo(origin, current);
            return result;
        });
    };
    router.go = delta => { void traverse(delta); };
    router.back = () => router.go(-1);
    router.forward = () => router.go(1);
    const pageBoundary = () => { cancelRequest({ type: 'obsolete' }); committed = readEntry(); refreshUnloadProtection(); };
    window.addEventListener('pagehide', pageBoundary);
    window.addEventListener('pageshow', pageBoundary);

    const adapter: EditSessionNavigation = {
        navigate, handoff, traverse, refreshUnloadProtection,
        registerHost(host) {
            const registration: RegisteredHost = { host, live: true };
            hosts.push(registration);
            refreshUnloadProtection();
            return {
                dispose() {
                    registration.live = false;
                    const index = hosts.indexOf(registration);
                    if (index >= 0) hosts.splice(index, 1);
                    if (pending && (pending.owner === registration || pending.hosts.includes(registration)) && (!pending.routing || router.currentRoute === pending.origin)) cancelRequest({ type: 'obsolete' });
                    refreshUnloadProtection();
                },
                continueNavigation: (location, mode = 'push') => registration.live ? navigate(location, mode, registration) : Promise.resolve({ type: 'obsolete' }),
                continueHandoff: (target, run) => registration.live ? handoff(target, run, registration) : Promise.resolve({ type: 'obsolete' }),
            };
        },
        dispose() {
            if (disposed) return;
            disposed = true;
            cancelRequest({ type: 'obsolete' });
            stopArrival();
            removeGuard();
            window.removeEventListener('popstate', nativePop, true);
            window.removeEventListener('beforeunload', beforeUnload);
            window.removeEventListener('pagehide', pageBoundary);
            window.removeEventListener('pageshow', pageBoundary);
            if (window.history.pushState === wrappedPushState) window.history.pushState = pushState;
            if (window.history.replaceState === wrappedReplaceState) window.history.replaceState = replaceState;
            router.push = originalPush;
            router.replace = originalReplace;
            router.go = originalGo;
            router.back = originalBack;
            router.forward = originalForward;
            history.push = originalHistoryPush;
            history.replace = originalHistoryReplace;
            history.transitionTo = originalTransition;
            history.ensureURL = originalEnsureURL;
            hosts.splice(0);
            adapters.delete(router);
        },
    };
    adapters.set(router, adapter);
    return adapter;
}
